import React, { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import {
  getIngredients,
  selectIngredients,
} from "../../redux/features/ingredient/IngredientSlice";
import {
  getConsumerItem,
  selectConsumerItems,
} from "../../redux/features/item/ItemSlice";
import { toast } from "react-toastify";
import Navbar from "../Navbar/Navbar";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  getInventoryAdjusts,
  inventoryAdjust,
  RESET_INVENTORY_ADJUST,
  selectCreateInventoryAdjustSuccess,
} from "../../redux/features/inventoryAdjust/InventoryAdjustSlice";

const CreateInventoryAdjustment = () => {

  const ingredients = useSelector(selectIngredients);
  const items = useSelector(selectConsumerItems);
  const isSuccess = useSelector(selectCreateInventoryAdjustSuccess);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [fields, setFields] = useState([
    {
      ingredientOut: 0,
      ingredientIn: 0,
      ingredientName: "",
      ingredientId: "",
      purchaseUnit: "",
      unitOfMeasure: "",
      conversionRate: 0,
    },
  ]);
  const [error, setError] = useState([]);

  const handleAddField = () => {
    setFields([
      ...fields,
      {
        ingredientOut: 0,
        ingredientIn: 0,
        ingredientName: "",
        ingredientId: "",
        purchaseUnit: "",
        unitOfMeasure: "",
        conversionRate: 0,
      },
    ]);
    setError([...error, {}]);
  };

  const handleRemoveField = (index) => {
    if (fields.length > 0) {
      setFields(fields.filter((_, i) => i !== index));
      setError(error.filter((_, i) => i !== index));
    }
  };

  const handleChange = (index, name, value) => {
    const updatedFields = [...fields];
    if (name === "ingredient") {
      const selectedIngredient = ingredients.find(
        (ingredient) => ingredient._id === value
      );

      updatedFields[index] = {
        ...updatedFields[index],
        ingredientId: selectedIngredient?._id,
        ingredientName: selectedIngredient?.name,
        unitOfMeasure: selectedIngredient?.inventoryUnit,
        conversionRate: selectedIngredient?.conversionRate,
        purchaseUnit: selectedIngredient?.purchaseUnit,
      };
    } else if (name === "ingredientIn") {
      updatedFields[index][name] = Number(value);
    } else {
      updatedFields[index][name] = Number(value);
    }
    setFields(updatedFields);

    // Clear errors when a field is updated
    const updatedErrors = [...error];
    updatedErrors[index] = { ...updatedErrors[index], [name]: "" };
    setError(updatedErrors);
  };

  const [fieldItems, setFieldItems] = useState([
    {
      itemOut: 0,
      itemIn: 0,
      itemName: "",
      itemId: "",
      purchaseUnit: "",
      unitOfMeasure: "",
      conversionRate: 0,
    },
  ]);
  const [errorItem, setErrorItem] = useState([]);

  const handleAddFieldItem = () => {
    setFieldItems([
      ...fieldItems,
      {
        itemOut: 0,
        itemIn: 0,
        itemName: "",
        itemId: "",
        purchaseUnit: "",
        unitOfMeasure: "",
        conversionRate: 0,
      },
    ]);
    setErrorItem([...errorItem, {}]);
  };

  const handleRemoveFieldItem = (index) => {
    if (fieldItems.length > 0) {
      setFieldItems(fieldItems.filter((_, i) => i !== index));
      setErrorItem(errorItem.filter((_, i) => i !== index));
    }
  };

  const handleChangeItem = (index, name, value) => {
    const updatedFieldItems = [...fieldItems];
    if (name === "item") {
      const selectedItem = items.find((item) => item._id === value);

      updatedFieldItems[index] = {
        ...updatedFieldItems[index],
        itemId: selectedItem?._id,
        itemName: selectedItem?.name,
        unitOfMeasure: selectedItem?.inventoryUnit,
        conversionRate: selectedItem?.conversionRate,
        purchaseUnit: selectedItem?.purchaseUnit,
      };
    } else if (name === "itemIn") {
      updatedFieldItems[index][name] = Number(value);
    } else {
      updatedFieldItems[index][name] = Number(value);
    }
    setFieldItems(updatedFieldItems);

    // Clear errors when a field is updated
    const updatedErrorItems = [...errorItem];
    updatedErrorItems[index] = { ...updatedErrorItems[index], [name]: "" };
    setErrorItem(updatedErrorItems);
  };

  const validateFields = () => {
    if (fields.length === 0) {
      setFields([]);
      return true;
    }
    const seenIngredients = new Map();
    const newErrors = fields.map((field, index) => {
      const fieldErrors = {};

      if (!field.ingredientName) {
        fieldErrors.ingredientName = "Ingredient is required.";
      } else {
        if (seenIngredients.has(field.ingredientId)) {
          fieldErrors.ingredientName =
            "Ingredient already exists. Please update the quantity.";
        } else {
          seenIngredients.set(field.ingredientId, index);
        }
      }

      if (
        (!field.ingredientIn ||
          isNaN(field.ingredientIn) ||
          Number(field.ingredientIn) <= 0) &&
        (!field.ingredientOut ||
          isNaN(field.ingredientOut) ||
          Number(field.ingredientOut) <= 0)
      ) {
        fieldErrors.ingredientIn =
          "Either 'Ingredient In' or 'Ingredient Out' must be a positive number.";
      }

      return fieldErrors;
    });

    setError(newErrors);

    return newErrors.every((error) => Object.keys(error).length === 0);
  };

  const validateFieldItems = () => {
    if (fieldItems.length === 0) {
      setFieldItems([]);
      return true;
    }
    const seenItems = new Map();
    const newErrors = fieldItems.map((fieldItem, index) => {
      const fieldErrors = {};

      if (!fieldItem.itemName) {
        fieldErrors.itemName = "Item is required.";
      } else {
        if (seenItems.has(fieldItem.itemId)) {
          fieldErrors.itemName =
            "Item already exists. Please update the quantity.";
        } else {
          seenItems.set(fieldItem.itemId, index);
        }
      }

      if (
        (!fieldItem.itemIn ||
          isNaN(fieldItem.itemIn) ||
          Number(fieldItem.itemIn) <= 0) &&
        (!fieldItem.itemOut ||
          isNaN(fieldItem.itemOut) ||
          Number(fieldItem.itemOut) <= 0)
      ) {
        fieldErrors.itemIn =
          "Either 'Item In' or 'Item Out' must be a positive number.";
      }

      return fieldErrors;
    });

    setErrorItem(newErrors);

    return newErrors.every((error) => Object.keys(error).length === 0);
  };

  const handleSubmit = async () => {
    const fieldItemsValid = validateFieldItems();
    const fieldsValid = validateFields();

    if (!fieldItemsValid || !fieldsValid) {
      console.error("Validation failed. Please check the errors.");
      return;
    }

    if (fieldItems.length > 0 || fields.length > 0) {
      const inventoryAdjustData = {
        ingredients: fields,
        items: fieldItems,
      };
      //   console.log(purchaseData);

      await dispatch(inventoryAdjust(inventoryAdjustData));
    } else {
      toast.error("At least one ingredient or item is required.");
    }
  };

  useEffect(() => {
    if (isSuccess) {
      dispatch(getInventoryAdjusts());
      dispatch(RESET_INVENTORY_ADJUST());
      navigate("/purchases");
    }
  }, [isSuccess, dispatch, navigate]);

  useEffect(() => {
    dispatch(getIngredients());
    dispatch(getConsumerItem());
  }, [dispatch]);

  const itemOptions = items?.map((item) => ({
    value: item._id,
    label: item.name,
  }));

  const ingredientOptions = ingredients?.map((ingredient) => ({
    value: ingredient._id,
    label: ingredient.name,
  }));

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: "none",
      borderBottom: "1px solid #0D868C",
      borderRadius: "0px",
      boxShadow: "none",
      outline: "none",
      backgroundColor: "transparent",
      minHeight: "30px",
      padding: "none",
      textTransform: "capitalize",
      cursor: "pointer",
      "&:hover": {
        borderBottom: "1px solid #0D868C",
        boxShadow: "none",
      },
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 130,
      overflowY: "auto",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#A9A9A9",
      fontSize: "0.9rem",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#A9A9A9",
      fontSize: "0.9rem",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      display: "none",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "4px",
      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#0D868C"
        : state.isFocused
        ? "#E0F7FA"
        : "white",
      color: state.isSelected ? "white" : "#0D868C",
      padding: "4px",
      fontSize: "0.9rem",
      textTransform: "capitalize",
    }),
  };

  return (
    <div className="flex flex-col h-screen bg-[#F1F1F1] overflow-hidden">
      <Navbar page="Create Inventory Adjustment" />

      <div className="relative overflow-y-auto  scrollbar-hide">
        <div className="flex items-center justify-center my-10">
          <div className="bg-white shadow-xl rounded-[10px] mb-10 md:w-[80%] lg:w-[70%] w-[90%]   mx-auto  p-5 space-y-4">
            <div className="flex justify-between items-center border-b-2 border-[#DEDEDE] pb-2 mb-3">
              <h1 className="md:text-[1.5rem] text-[1rem] font-bold text-[#0D868C]">
                Create Inventory Adjustment
              </h1>
            </div>

            <>
              <div className="mb-3 flex flex-col items-start mt-1 gap-2">
                {fields.map((field, index) => (
                  <div key={index} className="w-full mb-3">
                    <div key={index} className="w-full flex items-end gap-2 ">
                      {/* Select Dropdown */}
                      <div className=" flex flex-col items-start  w-[30%]">
                        <label className="text-[.7rem] font-semibold text-[#7D7D7D]">
                          Ingredient
                        </label>

                        <Select
                          options={ingredientOptions}
                          value={
                            ingredientOptions.find(
                              (opt) => opt.value === field.ingredientId
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            handleChange(
                              index,
                              "ingredient",
                              selectedOption?.value
                            )
                          }
                          placeholder="Search Ingredient"
                          styles={customStyles}
                          isSearchable
                          className="w-full text-[.9rem] font-normal text-[#A9A9A9] outline-none"
                        />
                      </div>

                      <div className="flex-1  ">
                        <div className="flex items-end justify-between gap-2">
                          <div className="flex-grow  flex flex-col items-start ">
                            <label className="text-[.7rem] font-semibold text-[#7D7D7D]">
                              Quantity In
                            </label>
                            <input
                              type="number"
                              name="ingredientIn"
                              min={0}
                              step={0.01}
                              onWheel={(e) => e.target.blur()}
                              value={field.ingredientIn}
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  "ingredientIn",
                                  e.target.value
                                )
                              }
                              placeholder="Enter the quantity In"
                              className="w-full outline-none rounded-none text-[.9rem] font-normal text-[#A9A9A9] pr-3 pt-1 border-b border-[#0D868C]"
                            />
                          </div>
                          <div className="flex-grow  flex flex-col items-start ">
                            <label className="text-[.7rem] font-semibold text-[#7D7D7D]">
                              Quantity Out
                            </label>
                            <input
                              type="number"
                              name="ingredientOut"
                              min={0}
                              step={0.01}
                              onWheel={(e) => e.target.blur()}
                              value={field.ingredientOut}
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  "ingredientOut",
                                  e.target.value
                                )
                              }
                              placeholder="Enter the quantity Out"
                              className="w-full outline-none rounded-none text-[.9rem] font-normal text-[#A9A9A9] pr-3 pt-1 border-b border-[#0D868C]"
                            />
                          </div>
                          <div className="flex-grow flex items-start ">
                            <div className="flex flex-col flex-1">
                              <label
                                title="Purchase Unit of measure"
                                className="text-[.7rem] font-semibold text-[#7D7D7D]"
                              >
                                Purchase MOU
                              </label>
                              <input
                                type="text"
                                value={field.purchaseUnit}
                                readOnly
                                placeholder="Purchase unit"
                                className="w-full outline-none rounded-none text-[.9rem] font-normal bg-[#f5f4f4]  text-[#A9A9A9] pr-3 pt-1 border-b border-[#0D868C]"
                              />
                            </div>
                          </div>
                          {fields.length > 0 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveField(index)}
                              className="bg-red-500 text-white rounded-md  flex-none"
                            >
                              <FiX className="lg:size-8 size-6" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex w-full flex-col">
                      {error[index]?.ingredientName && (
                        <span className="flex-1 text-red-500 text-sm">
                          {error[index].ingredientName}
                        </span>
                      )}
                      {error[index]?.ingredientIn && (
                        <span className="flex-1 text-red-500 text-sm">
                          {error[index].ingredientIn}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddField}
                  className=" capitalize border text-[.9rem] flex items-center justify-center border-[#0D868C] text-[#0D868C] px-3 py-2 rounded-full cursor-pointer "
                >
                  <FaPlus className="size-4 mr-3" />
                  Add Ingredient
                </button>
              </div>
              <div className="mb-3 flex flex-col items-start mt-1 gap-2">
                {fieldItems.map((fieldItem, index) => (
                  <div key={index} className="w-full mb-3">
                    <div key={index} className="w-full flex items-end gap-2 ">
                      <div className="flex flex-col items-start w-[30%]">
                        <label className="text-[.7rem] font-semibold text-[#7D7D7D]">
                          Item
                        </label>
                        <Select
                          options={itemOptions}
                          value={
                            itemOptions.find(
                              (opt) => opt.value === fieldItem.itemId
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            handleChangeItem(
                              index,
                              "item",
                              selectedOption?.value
                            )
                          }
                          placeholder="Search Item"
                          styles={customStyles}
                          isSearchable
                          className="w-full text-[.9rem] font-normal text-[#A9A9A9] outline-none"
                        />
                      </div>

                      <div className="flex-1  ">
                        <div className="flex items-end justify-between gap-2">
                          <div className="flex-grow  flex flex-col items-start ">
                            <label className="text-[.7rem] font-semibold text-[#7D7D7D]">
                              Quantity In
                            </label>
                            <input
                              type="number"
                              name="itemIn"
                              min={0}
                              onWheel={(e) => e.target.blur()}
                              value={fieldItem.itemIn}
                              onChange={(e) =>
                                handleChangeItem(
                                  index,
                                  "itemIn",
                                  e.target.value
                                )
                              }
                              placeholder="Enter the quantity In"
                              className="w-full outline-none rounded-none text-[.9rem] font-normal text-[#A9A9A9] pr-3 pt-1 border-b border-[#0D868C]"
                            />
                          </div>
                          <div className="flex-grow  flex flex-col items-start ">
                            <label className="text-[.7rem] font-semibold text-[#7D7D7D]">
                              Quantity Out
                            </label>
                            <input
                              type="number"
                              name="itemOut"
                              min={0}
                              onWheel={(e) => e.target.blur()}
                              value={fieldItem.itemOut}
                              onChange={(e) =>
                                handleChangeItem(
                                  index,
                                  "itemOut",
                                  e.target.value
                                )
                              }
                              placeholder="Enter the quantity Out"
                              className="w-full outline-none rounded-none text-[.9rem] font-normal text-[#A9A9A9] pr-3 pt-1 border-b border-[#0D868C]"
                            />
                          </div>

                          <div className="flex-grow flex items-start ">
                            <div className="flex flex-col flex-1">
                              <label
                                title="Purchase Unit of measure"
                                className="text-[.7rem] font-semibold text-[#7D7D7D]"
                              >
                                Purchase MOU
                              </label>
                              <input
                                type="text"
                                value={fieldItem.purchaseUnit}
                                readOnly
                                placeholder="Purchase unit"
                                className="w-full outline-none rounded-none text-[.9rem] font-normal bg-[#f5f4f4] text-[#A9A9A9] pr-3 pt-1 border-b border-[#0D868C]"
                              />
                            </div>
                          </div>

                          {/* Remove Button */}
                          {fieldItems.length > 0 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveFieldItem(index)}
                              className="bg-red-500 text-white rounded-md  flex-none"
                            >
                              <FiX className="lg:size-8 size-6" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex w-full flex-col">
                      {errorItem[index]?.itemName && (
                        <span className="flex-1 text-red-500 text-sm">
                          {errorItem[index].itemName}
                        </span>
                      )}
                      {errorItem[index]?.itemIn && (
                        <span className="flex-1 text-red-500 text-sm">
                          {errorItem[index].itemIn}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddFieldItem}
                  className=" capitalize text-[.9rem] border flex items-center justify-center border-[#0D868C] text-[#0D868C] px-3 py-2 rounded-full cursor-pointer "
                >
                  <FaPlus className="size-4 mr-3" />
                  Add Item
                </button>
              </div>
            </>
            <div className="text-center mt-6">
              <button
                onClick={handleSubmit}
                className="text-white bg-[#0D868C] lg:w-[50%] md:w-[65%] w-[90%] py-3 rounded-full outline-none uppercase font-normal text-[1rem]"
              >
                Create Inventory Adjustment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateInventoryAdjustment;
